import type { CropMonitoring } from '/@/types/crop-monitoring';

export const AVAILABLE_CROPS = ['corn', 'wheat', 'soy', 'barley'] as const;
export const MIN_ENVIRONMENT = 1000;
export const MAX_ENVIRONMENT = 15000;

/** Density range (seeds/ha) per crop — wheat and barley share the same range. */
export const DENSITY_RANGE_BY_CROP: Record<(typeof AVAILABLE_CROPS)[number], { min: number; max: number }> = {
    corn: { min: 30000, max: 120000 },
    soy: { min: 20000, max: 40000 },
    wheat: { min: 2500000, max: 4000000 },
    barley: { min: 2500000, max: 4000000 },
};

/** Falls back to corn's range while no crop is selected yet. */
export const getDensityRange = (crop: (typeof AVAILABLE_CROPS)[number] | 'other' | '' | undefined) =>
    DENSITY_RANGE_BY_CROP[crop as (typeof AVAILABLE_CROPS)[number]] ?? DENSITY_RANGE_BY_CROP.corn;

/** Up to 5 hybrids can be added by hand per lote-campaign (CUL-1505); FieldView ones don't count. */
export const MAX_HYBRIDS = 5;

/** Per-hybrid planting data. */
export type HybridFormType = {
    /** CropMonitoring id when this hybrid already exists (edit); omit to create one. */
    id?: number;
    plantingDate: string | undefined;
    product: number | undefined | string;
    technology: number | undefined;
    environment: number | undefined;
    plantedArea: number | undefined;
    density: number | undefined;
    /** Created by FieldView from one seed of a planting report: doesn't count toward MAX_HYBRIDS. */
    fromFieldview?: boolean;
};

export const emptyHybrid = (): HybridFormType => ({
    plantingDate: undefined,
    product: undefined,
    technology: undefined,
    environment: undefined,
    plantedArea: undefined,
    density: undefined,
});

/** One active CropMonitoring of the campaign -> its editable hybrid form values. */
export const toFormHybrid = (cm: CropMonitoring): HybridFormType => ({
    id: cm.id,
    plantingDate: cm.plantingDate,
    product: cm.cornCropMonitoring?.product ?? cm.productName,
    technology: cm.cornCropMonitoring?.technology ?? undefined,
    environment: cm.environment ?? undefined,
    plantedArea: cm.plantedArea ?? undefined,
    density: cm.density ?? undefined,
    fromFieldview: !!cm.sourceSeedProductName,
});

/** Hybrids that count toward MAX_HYBRIDS, as the backend counts them. */
export const countHandMadeHybrids = (hybrids: HybridFormType[]) => hybrids.filter((h) => !h.fromFieldview).length;

export type CropMonitoringFormType = {
    id?: number;
    fieldId?: number;
    crop: (typeof AVAILABLE_CROPS)[number] | '';
    previousCrop?: (typeof AVAILABLE_CROPS)[number] | '' | 'none' | 'other';
    /** Multi-hybrid form values (the "Agregar cultivo" flow). */
    hybrids?: HybridFormType[];
    // Legacy single-hybrid fields, still used by the add-planting registry,
    // the density recommendation flow and the inline registry edit seeds.
    plantingDate?: string;
    product?: number | string;
    technology?: number;
    environment?: number;
    plantedArea?: number;
    density?: number;
};

export type FieldOption = {
    label: string;
    value: number;
};
